import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface OnboardingSettings {
  calendarConnected: "microsoft" | "google" | null;
  zoomConnected: boolean;
  meetingPreferences: {
    onlyOutsideParticipants: boolean;
    onlyMeetingsIOwn: boolean;
    allMeetingsWithVideoLink: boolean;
    onlyWhenInvited: boolean;
  };
  botName: string;
}

/**
 * A connect failure carried across the OAuth round trip.
 *
 * The callback page can only toast before it navigates back here, and a toast
 * is gone by the time the user is looking at the step that needs re-running.
 * Parking the reason in the store lets CalendarStep show it inline with the
 * retry attached — which for `expired` is the entire fix, since re-connecting
 * is all the user ever has to do.
 */
interface OnboardingError {
  message: string;
  type: "permissions" | "expired" | "unknown";
  /** Which provider failed, so the retry can go straight back to it. */
  platform?: "microsoft" | "google";
  timestamp: number;
}

interface OnboardingStore {
  currentStep: number;
  completedSteps: number[];
  settings: OnboardingSettings;
  error: OnboardingError | null;
  setCurrentStep: (step: number) => void;
  markStepCompleted: (step: number) => void;
  getNextUnfinishedStep: () => number | null;
  nextStep: () => void;
  previousStep: () => void;
  setCalendarConnected: (calendar: "microsoft" | "google" | null) => void;
  setZoomConnected: (connected: boolean) => void;
  setMeetingPreference: (key: keyof OnboardingSettings["meetingPreferences"], value: boolean) => void;
  setBotName: (name: string) => void;
  setError: (error: OnboardingError | null) => void;
  clearError: () => void;
  resetOnboarding: () => void;
}

const initialSettings: OnboardingSettings = {
  calendarConnected: null,
  zoomConnected: false,
  meetingPreferences: {
    onlyOutsideParticipants: false,
    onlyMeetingsIOwn: false,
    allMeetingsWithVideoLink: true,
    onlyWhenInvited: false,
  },
  botName: "Focal",
};

export const useOnboardingStore = create(
  persist<OnboardingStore>(
    (set, get) => ({
      currentStep: 1,
      completedSteps: [],
      settings: initialSettings,
      error: null,
      setCurrentStep: (step) => set({ currentStep: step }),
      markStepCompleted: (step) =>
        set((state) => ({
          completedSteps: state.completedSteps.includes(step)
            ? state.completedSteps
            : [...state.completedSteps, step].sort((a, b) => a - b),
        })),
      getNextUnfinishedStep: () => {
        const state = get();
        const totalSteps = 3;
        for (let step = 1; step <= totalSteps; step++) {
          if (!state.completedSteps.includes(step)) {
            return step;
          }
        }
        return null; // All steps completed, return null
      },
      nextStep: () => set((state) => ({ currentStep: Math.min(state.currentStep + 1, 3) })),
      previousStep: () => set((state) => ({ currentStep: Math.max(state.currentStep - 1, 1) })),
      setCalendarConnected: (calendar) =>
        set((state) => ({
          settings: { ...state.settings, calendarConnected: calendar },
        })),
      setZoomConnected: (connected) =>
        set((state) => ({
          settings: { ...state.settings, zoomConnected: connected },
        })),
      setMeetingPreference: (key, value) =>
        set((state) => ({
          settings: {
            ...state.settings,
            meetingPreferences: {
              ...state.settings.meetingPreferences,
              [key]: value,
            },
          },
        })),
      setBotName: (name) =>
        set((state) => ({
          settings: { ...state.settings, botName: name },
        })),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      resetOnboarding: () =>
        set({
          currentStep: 1,
          completedSteps: [],
          settings: initialSettings,
          error: null,
        }),
    }),
    {
      name: "onboarding-storage",
      storage: createJSONStorage(() => localStorage),
    }
  )
);